'use client';

import React, { useMemo } from 'react';

/**
 * ca_preview only: the image well for a listing with no photograph of its
 * own. The store's own "no picture" language (Warm Paper ground, the
 * contour lines the store's app strip uses), a glyph for what kind of place
 * it is and its type in words. It never pretends to be a photo or a map of
 * the place: the lines are decoration, drawn from the listing's id so each
 * card differs and the same card always looks the same.
 */

function seeded(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

/** Closed, gently wobbling rings around (cx, cy), as SVG path data. Exported for tests. */
export function contourPaths(seed: string, rings = 7): string[] {
  const rnd = seeded(seed || 'place');
  const cx = 40 + rnd() * 120;
  const cy = 30 + rnd() * 90;
  const p1 = rnd() * Math.PI * 2;
  const p2 = rnd() * Math.PI * 2;
  const a1 = 0.1 + rnd() * 0.1;
  const a2 = 0.05 + rnd() * 0.08;
  const out: string[] = [];
  for (let k = 1; k <= rings; k++) {
    const r = 14 + k * 17;
    const pts: string[] = [];
    for (let i = 0; i < 36; i++) {
      const t = (i / 36) * Math.PI * 2;
      const rr = r * (1 + a1 * Math.sin(2 * t + p1 + k * 0.25) + a2 * Math.sin(3 * t + p2 - k * 0.2));
      pts.push(`${(cx + rr * Math.cos(t) * 1.25).toFixed(1)},${(cy + rr * Math.sin(t)).toFixed(1)}`);
    }
    out.push(`M${pts.join('L')}Z`);
  }
  return out;
}

function Glyph({ type }: { type?: string | null }) {
  const t = (type || '').toLowerCase();
  if (/caravan|holiday/.test(t)) {
    return (
      <path d="M3 15V8.5A2.5 2.5 0 0 1 5.5 6H16a4 4 0 0 1 4 4v5H3Zm0 0h18M8 15v2m-2-7h4v3H6v-3Zm7 0h3v5h-3" />
    );
  }
  if (/hut|cabin|lodge|accom|motel|hotel|cottage/.test(t)) {
    return <path d="M4 11 12 5l8 6v8H4v-8Zm6 8v-5h4v5" />;
  }
  if (/rest|fuel|stop/.test(t)) {
    return <path d="M5 19V6a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v13M3 19h13M14 10h2a2 2 0 0 1 2 2v4a1 1 0 0 0 2 0V9l-3-3M7 9h5" />;
  }
  // Tent: campgrounds, free camps, bush camps.
  return <path d="M12 5 3 19h18L12 5Zm0 0v14m-3 0 3-5 3 5" />;
}

export default function PlaceArt({
  seed,
  type,
  label,
  className = '',
  size = 'sm',
}: {
  seed: string;
  type?: string | null;
  /** The listing's type in words, under the glyph (e.g. "Free camp"). */
  label?: string;
  className?: string;
  size?: 'sm' | 'lg';
}) {
  const paths = useMemo(() => contourPaths(seed), [seed]);
  return (
    <div className={`ca-placeart ca-placeart--${size} ${className}`} data-testid="place-art" aria-hidden="true">
      <svg className="ca-placeart__lines" viewBox="0 0 200 150" preserveAspectRatio="xMidYMid slice">
        {paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
      <span className="ca-placeart__badge">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <Glyph type={type} />
        </svg>
      </span>
      {label && <span className="ca-placeart__label">{label}</span>}
    </div>
  );
}
